import Konva from 'konva';
import React from 'react';
import { Rect } from 'react-konva';
import { getSnapPosition } from '../../../../utils/snap';
import {
  hideShapeToolBoxes,
  showShapeToolBoxes
} from '../../../../utils/toolbox';
import { GroupShape as GroupShapeComponent } from '../../../GroupShape';
import { SnapLines } from '../../../SnapLines';
import { useMapCanvas } from '../../contexts/MapCanvasContext';
import { MapEditorState } from '../../contexts/MapEditorContext';
import { SELECTION_STROKE_COLOR } from '../../defines';
import { ShapeCommonProps } from '../../types';
import { CircleShape } from '../CircleShape/CircleShape';
import { FurnitureShape } from '../FurnitureShape/FurnitureShape';
import { IconShape } from '../IconShape/IconShape';
import { RectShape } from '../RectShape/RectShape';
import { useShape } from '../ShapeContext/useShape';
import { ShapeTransformer } from '../ShapeTransformer';
import { SpotShapeComponent } from '../SpotShape/SpotShape';
import { SvgShape } from '../SvgShape/SvgShape';
import { TextShape } from '../TextShape/TextShape';
import { Shape, CircleShape as ICircleShape } from '../../../../types';
import { ImageShape } from '../ImageShape/ImageShape';

export interface DraggableShapeProps {
  setContextMenuShape?: MapEditorState['setContextMenuShape'];
  setHighlightedShape?: MapEditorState['setHighlightedShape'];
  highlightedShape?: MapEditorState['highlightedShape'];
  onStartDragDuplicate?: () => void;
}

export const DraggableShape: React.FC<DraggableShapeProps> = ({
  setContextMenuShape,
  setHighlightedShape,
  highlightedShape,
  onStartDragDuplicate
}) => {
  const { gridSize, isViewMode, isDragging, stagePosition } = useMapCanvas();
  const {
    isEditing,
    setSnapLines,
    snapLines,
    setIsHovered,
    isHovered,
    shape,
    isSelected,
    onSelect,
    onUpdate,
    isGroupChild,
    snapToGrid,
    otherShapes,
    showSnapLines,
    disabled,
    isMultiSelected
  } = useShape();
  const shapeRef = React.useRef<any>(null);
  const transformerRef =
    React.useRef<React.ElementRef<typeof ShapeTransformer>>(null);

  React.useEffect(() => {
    if (
      isSelected &&
      !isMultiSelected &&
      transformerRef.current &&
      shapeRef.current
    ) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected, isMultiSelected, shape]);

  // The shape is hidden, don't render it
  if (shape.hidden) {
    return null;
  }

  const snapToGridValue = (value: number): number => {
    if (!snapToGrid) return value;
    return Math.round(value / gridSize) * gridSize;
  };

  const handleDragStart = (event: Konva.KonvaEventObject<DragEvent>) => {
    hideShapeToolBoxes();
    if (event.evt.altKey) {
      onStartDragDuplicate?.();
    }

    if (!isSelected && !disabled && !shape.locked) {
      onSelect(event);
    }
  };

  const handleDragMove = (event: Konva.KonvaEventObject<DragEvent>) => {
    if (isMultiSelected) return;
    if (isGroupChild || disabled || shape.locked) return;

    const node = event.target;
    let newX = node.x();
    let newY = node.y();

    if (snapToGrid) {
      newX = snapToGridValue(newX);
      newY = snapToGridValue(newY);
      node.position({ x: newX, y: newY });
    }

    if (showSnapLines) {
      const snapResult = getSnapPosition(
        shape,
        otherShapes || [],
        { x: newX, y: newY },
        shape.id
      );

      if (snapResult.position.x !== undefined) {
        newX = snapResult.position.x;
      }
      if (snapResult.position.y !== undefined) {
        newY = snapResult.position.y;
      }

      setSnapLines(snapResult.lines);
    } else {
      setSnapLines({});
    }

    node.position({ x: newX, y: newY });
  };

  const handleDragEnd = (event: Konva.KonvaEventObject<DragEvent>) => {
    showShapeToolBoxes();
    if (!isGroupChild && !disabled && !shape.locked) {
      const node = event.target;
      onUpdate({
        x: node.x(),
        y: node.y()
      });
    }
    setSnapLines({});
  };

  const handleTransformEnd = () => {
    if (!isGroupChild && !disabled && !shape.locked) {
      const node = shapeRef.current;

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      node.scaleX(1);
      node.scaleY(1);

      const updateData: Partial<Shape> = {
        x: node.x(),
        y: node.y(),
        width: Math.max(5, node.width() * scaleX),
        height: Math.max(5, node.height() * scaleY),
        rotation: node.rotation()
      };

      if (shape.type === 'circle') {
        const newRadius =
          Math.max(updateData.width || 0, updateData.height || 0) / 2;
        updateData.width = (updateData.width || 0) / 2;
        updateData.height = (updateData.height || 0) / 2;
        (updateData as ICircleShape).radius = newRadius;
      }

      onUpdate(updateData);
    }
  };

  const handleContextMenu = () => {
    if (shape.locked) {
      return;
    }
    setContextMenuShape?.(shape);
  };

  const handleOnClick = (event: Konva.KonvaEventObject<MouseEvent>) => {
    if (shape.locked) {
      return;
    }

    onSelect(event);
  };

  const handleMouseEnter = () => {
    if (isViewMode || shape.locked) return;
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    if (isViewMode || shape.locked) return;
    setIsHovered(false);
  };

  const commonProps: ShapeCommonProps = {
    id: `shape-${shape.id}`,
    x: shape.x,
    y: shape.y,
    draggable:
      !isDragging && !isViewMode && !isGroupChild && !disabled && !shape.locked,
    rotation: shape.rotation || 0,
    onClick: handleOnClick,
    onTap: onSelect,
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    onTransformEnd: handleTransformEnd,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onContextMenu: handleContextMenu,
    highlightShapeData:
      highlightedShape?.shape?.id === shape.id ? highlightedShape : undefined,
    ref: shapeRef
  };

  if (shape.type === 'group') {
    return <GroupShapeComponent {...commonProps} />;
  }

  return (
    <>
      {shape.type === 'spot' ? (
        <SpotShapeComponent
          highlightShape={setHighlightedShape}
          {...commonProps}
        />
      ) : shape.type === 'circle' ? (
        <CircleShape {...commonProps} />
      ) : shape.type === 'text' ? (
        <TextShape {...commonProps} highlightShape={setHighlightedShape} />
      ) : shape.type === 'image' ? (
        <ImageShape {...commonProps} />
      ) : shape.type === 'svg' ? (
        <SvgShape {...commonProps} />
      ) : shape.type === 'furniture' ? (
        <FurnitureShape {...commonProps} />
      ) : shape.type === 'icon' ? (
        <IconShape {...commonProps} />
      ) : (
        <RectShape {...commonProps} />
      )}

      {isHovered && !isSelected && shape.type !== 'spot' && (
        <Rect
          stroke={SELECTION_STROKE_COLOR}
          strokeWidth={1}
          x={shape.type === 'circle' ? shape.x - shape.width : shape.x}
          y={shape.type === 'circle' ? shape.y - shape.height : shape.y}
          rotation={shape.rotation}
          width={shape.type === 'circle' ? shape.width * 2 : shape.width}
          height={shape.type === 'circle' ? shape.height * 2 : shape.height}
          listening={false}
        />
      )}

      {isSelected &&
        !isEditing &&
        !isGroupChild &&
        !disabled &&
        !isMultiSelected &&
        !shape.locked && (
          <ShapeTransformer
            ref={transformerRef}
            resizeEnabled={shape.type !== 'spot'}
          />
        )}
      {!isGroupChild && showSnapLines && <SnapLines snapLines={snapLines} />}
    </>
  );
};
